// Imports
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '../../../../shareables/foundation/admin-ui/components/content/box';
import Heading from '../../../../shareables/foundation/admin-ui/components/content/heading';
import { useAppDispatch } from '../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../shareables/integrations/api/redux/actions';
import Select from '../../../../shareables/foundation/front-end/components/forms/fields/select';
import { GetEligibleResponse } from '../../../../shareables/types/api/main/reseller/websites/eligible/get';
import ResellerContext from '../../../resources/reseller-provider/context';
import AnimateHeight from 'react-animate-height';
import styled from 'styled-components/macro';
import { fieldContainerBottomMargin } from '../../../../shareables/foundation/front-end/components/forms/field-container';
import PlanSelector from '../../../../shareables/front-end/subscription-plans/plan-selector';


// Styled components
const PlansWrapper = styled(Box)`
	margin-bottom: 1.5rem;
`;

const Charge = styled(Box)`
	margin-bottom: ${fieldContainerBottomMargin};
`;


// Define the accepted props
interface Props {
	storybookWebsites?: GetEligibleResponse;
	storybookSelectedWebsiteID?: GetEligibleResponse[0]['id'];
}


// Function component
const AdoptWebsite: React.FC<Props> = ({ storybookWebsites, storybookSelectedWebsiteID }) => {
	// Use state
	const [websites, setWebsites] = useState<GetEligibleResponse | null>(storybookWebsites ?? null);
	const [selectedWebsiteID, setSelectedWebsiteID] = useState<GetEligibleResponse[0]['id'] | null>(
		storybookSelectedWebsiteID ?? null
	);
	
	const [selectedPlan, setSelectedPlan] = useState(0);
	const [selectedPlanChanged, setSelectedPlanChanged] = useState(false);
	const [planErrorMessage, setPlanErrorMessage] = useState('');
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const navigate = useNavigate();
	
	
	// Use context
	const reseller = useContext(ResellerContext);
	
	
	// Load websites on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetEligibleResponse>({
				action: 'GET',
				uri: `/reseller/${reseller?.id ?? ''}/websites/eligible`,
				completion: (json) => {
					setWebsites(json.response);
				},
			})
		);
	}, [reseller?.id, dispatch]);
	
	
	// Build form options
	const formOptions: APIFormOptions<undefined, undefined, undefined> = {
		action: 'ADOPT',
		uri: `/reseller/${reseller?.id ?? ''}/website/[websiteID]`,
		submitButton: {
			text: 'Adopt',
		},
		errorHandlers: [
			{
				match: {
					code: 'PARAMETER_ERROR',
					parameter: 'plan',
				},
				handler: (json) => {
					if (json.message.toLowerCase().includes('this is required')) {
						setPlanErrorMessage('Please select a plan above.');
						return;
					}
					
					setPlanErrorMessage(json.message);
				},
			},
		],
	};
	
	
	// Initialize
	const selectedWebsite = websites?.find((website) => website.id === selectedWebsiteID);
	
	
	// If loading, then display nothing
	if (websites === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Heading size={1}>Adopt website</Heading>
			
			<APIForm options={formOptions} onCompletion={() => navigate('..')} onCancel={() => navigate(-1)}>
				<Box>
					<Select
						name='websiteID'
						label='Website to adopt'
						placeholder={websites.length === 0 ? 'No websites eligible for adoption…' : 'Please select…'}
						icon={faGlobe}
						disabled={websites.length === 0}
						required
						onChange={(newValue) => {
							setSelectedWebsiteID(Number(newValue));
							setSelectedPlan(0);
							setSelectedPlanChanged(false);
							setPlanErrorMessage('');
						}}
						initialValue={storybookSelectedWebsiteID}
					>
						{websites.map((website) => (
							<option key={website.id} value={website.id}>
								{website.communityName}
							</option>
						))}
					</Select>
				</Box>
				
				<AnimateHeight
					height={selectedWebsite?.requiresImmediateTransaction ? 'auto' : 0}
					duration={400}
					animateOpacity
				>
					<PlansWrapper>
						<PlanSelector
							fieldLabel='Subscription plan'
							currentUsage={selectedWebsite?.eligibilityFactors.currentUsage}
							handleSelectedPlanChange={(newValue) => {
								setSelectedPlan(Number(newValue));
								setSelectedPlanChanged(true);
								setPlanErrorMessage('');
							}}
							selectedPlan={selectedPlan}
							selectedPlanChanged={selectedPlanChanged}
							errorMessage={planErrorMessage}
							resetKey={selectedWebsiteID ?? undefined}
						/>
					</PlansWrapper>
				</AnimateHeight>
				
				<AnimateHeight duration={400} height={selectedWebsite ? 'auto' : 0} animateOpacity>
					<Charge>
						You will be{' '}
						{selectedWebsite?.requiresImmediateTransaction
							? 'immediately charged for this website'
							: 'charged once this website’s existing subscription expires'}
						. Resellers with at least one resold website are required to spend a minimum of $100/mo.
					</Charge>
				</AnimateHeight>
			</APIForm>
		</React.Fragment>
	);
};

export default AdoptWebsite;
